import { useEffect, useMemo, useState } from "react";
import { createVoiceLine, fetchVoiceSettings, saveVoiceSettings } from "../voiceApi";
import type { VoiceBusinessHours, VoiceSettings } from "../types";
import { formatPhone } from "../phone";

// E.164: mirror the worker's POST /voice-settings guard so we 400 fast in the
// UI instead of bouncing through the network.
const E164 = /^\+[1-9]\d{7,14}$/;

const VOICE_DAYS: { key: string; label: string }[] = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
];

type Props = {
  isAdmin: boolean;
};

export function VoiceSettingsView({ isAdmin }: Props) {
  const readOnly = !isAdmin;

  const [rows, setRows] = useState<VoiceSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);

  // Per-row form state. The user edits a form copy; Save flushes back to the
  // row and the API. A dirty map lets the Save button enable only when needed.
  const [draft, setDraft] = useState<VoiceSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // "New line" modal state.
  const [newLineOpen, setNewLineOpen] = useState(false);
  const [newLinePhone, setNewLinePhone] = useState("");
  const [newLineForward, setNewLineForward] = useState("");
  const [newLineSubmitting, setNewLineSubmitting] = useState(false);
  const [newLineError, setNewLineError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    fetchVoiceSettings()
      .then((data) => {
        if (!alive) return;
        setRows(data);
        if (data.length > 0 && selectedId === null) {
          setSelectedId(data[0].id);
        }
      })
      .catch((err) => alive && setLoadError((err as Error).message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // Only load once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the selected row changes, rehydrate the draft from the row.
  useEffect(() => {
    if (selectedId === null) {
      setDraft(null);
      return;
    }
    const row = rows.find((r) => r.id === selectedId) ?? null;
    setDraft(row ? { ...row, business_hours: normalizeHours(row.business_hours) } : null);
    setSaved(false);
    setSaveError(null);
  }, [selectedId, rows]);

  const isDirty = useMemo(() => {
    if (!draft) return false;
    const row = rows.find((r) => r.id === draft.id);
    if (!row) return true;
    return JSON.stringify({ ...row, updated_at: undefined }) !== JSON.stringify({ ...draft, updated_at: undefined });
  }, [draft, rows]);

  function setField<K extends keyof VoiceSettings>(key: K, value: VoiceSettings[K]) {
    if (readOnly || !draft) return;
    setDraft({ ...draft, [key]: value });
  }

  function toggleDay(dayKey: string) {
    if (readOnly || !draft) return;
    const current = draft.business_hours ?? {};
    const entry = current[dayKey] ?? { open: "09:00", close: "21:00", enabled: false };
    const next: VoiceBusinessHours = {
      ...current,
      [dayKey]: { ...entry, enabled: !entry.enabled },
    };
    setDraft({ ...draft, business_hours: next });
  }

  function updateDayHours(dayKey: string, field: "open" | "close", value: string) {
    if (readOnly || !draft) return;
    const current = draft.business_hours ?? {};
    const entry = current[dayKey] ?? { open: "09:00", close: "21:00", enabled: true };
    const next: VoiceBusinessHours = {
      ...current,
      [dayKey]: { ...entry, [field]: value },
    };
    setDraft({ ...draft, business_hours: next });
  }

  function openNewLine() {
    setNewLinePhone("");
    setNewLineForward("");
    setNewLineError(null);
    setNewLineOpen(true);
  }

  async function handleCreateLine() {
    if (readOnly) return;
    const phone = newLinePhone.trim();
    const forward = newLineForward.trim();
    if (!E164.test(phone)) {
      setNewLineError("Phone line must be in E.164 format (e.g. +17575550100).");
      return;
    }
    if (!E164.test(forward)) {
      setNewLineError("Forward number must be in E.164 format (e.g. +17575550100).");
      return;
    }
    setNewLineSubmitting(true);
    setNewLineError(null);
    try {
      const created = await createVoiceLine({
        phone_line: phone,
        forward_to_number: forward,
      });
      setRows((prev) => {
        // Keep the phone_line-asc ordering the GET uses, so the picker doesn't
        // reshuffle on the next reload.
        const next = [...prev, created];
        next.sort((a, b) => a.phone_line.localeCompare(b.phone_line));
        return next;
      });
      setSelectedId(created.id);
      setNewLineOpen(false);
    } catch (err) {
      const msg = (err as Error).message;
      // Surface the specific worker error codes — they're short and meaningful.
      if (msg.includes("phone_line_already_exists")) {
        setNewLineError("A line with that phone number already exists.");
      } else if (msg.includes("phone_line_invalid")) {
        setNewLineError("Phone line must be in E.164 format.");
      } else if (msg.includes("forward_to_number_invalid")) {
        setNewLineError("Forward number must be in E.164 format.");
      } else if (msg.startsWith("403")) {
        setNewLineError("Admin access is required to provision new lines.");
      } else {
        setNewLineError(msg);
      }
    } finally {
      setNewLineSubmitting(false);
    }
  }

  async function handleSave() {
    if (readOnly || !draft) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const saved = await saveVoiceSettings(draft.id, draft);
      setRows((prev) => prev.map((r) => (r.id === saved.id ? saved : r)));
      setDraft({ ...saved, business_hours: normalizeHours(saved.business_hours) });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-tribal-muted">
        Loading voice settings…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center">
        <div className="max-w-md rounded-lg border border-red-500/40 bg-red-500/10 p-6">
          <h2 className="text-lg font-semibold text-red-200">Couldn't load voice settings</h2>
          <p className="mt-2 text-sm text-red-300">{loadError}</p>
        </div>
      </div>
    );
  }

  const isEmpty = rows.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto">
      <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-6">
        {/* Line picker — always rendered; even one-line setups get an obvious ID. */}
        <div className="flex items-center gap-2 rounded-xl border border-tribal-border bg-tribal-surface p-2">
          {rows.map((r) => {
            const active = r.id === selectedId;
            return (
              <button
                key={String(r.id)}
                type="button"
                onClick={() => setSelectedId(r.id)}
                className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "bg-tribal-red text-white"
                    : "bg-white/5 text-tribal-muted hover:text-white"
                }`}
              >
                <div className="text-xs uppercase tracking-wider opacity-80">Line</div>
                <div className="text-sm">{formatPhone(r.phone_line)}</div>
              </button>
            );
          })}
          {!readOnly && (
            <button
              type="button"
              onClick={openNewLine}
              title="Provision a new phone line"
              className="shrink-0 rounded-lg border border-dashed border-tribal-border px-3 py-2 text-sm font-medium text-tribal-muted transition-colors hover:border-tribal-red hover:text-white"
            >
              + New line
            </button>
          )}
        </div>

        {isEmpty && (
          <div className="rounded-xl border border-tribal-border bg-tribal-surface px-5 py-6 text-center text-sm text-tribal-muted">
            {readOnly
              ? "No voice lines configured yet. Ask an admin to provision one."
              : "No voice lines configured yet. Click + New line above to provision one."}
          </div>
        )}

        {!draft ? null : (
          <section className="overflow-hidden rounded-xl border border-tribal-border bg-tribal-surface">
            <div className="border-b border-tribal-border px-5 py-4">
              <h2 className="text-base font-semibold">Voice Call Settings</h2>
              <p className="mt-0.5 text-xs text-tribal-muted">
                Business hours, forwarding, and voicemail greetings for {formatPhone(draft.phone_line)}
              </p>
            </div>

            <div className="space-y-5 px-5 py-5">
              {/* Business hours */}
              <div>
                <label className="mb-2 block text-xs font-medium text-tribal-muted">
                  Call Business Hours
                </label>
                <div className="space-y-2">
                  {VOICE_DAYS.map(({ key, label }) => {
                    const day =
                      draft.business_hours?.[key] ?? { open: "09:00", close: "21:00", enabled: false };
                    return (
                      <div key={key} className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => toggleDay(key)}
                          disabled={readOnly}
                          className={`w-24 shrink-0 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${
                            day.enabled
                              ? "bg-tribal-red text-white"
                              : "bg-white/5 text-tribal-muted hover:text-white"
                          } ${readOnly ? "cursor-not-allowed" : ""}`}
                        >
                          {label}
                        </button>
                        {day.enabled ? (
                          <div className="flex flex-1 items-center gap-2">
                            <input
                              type="time"
                              value={day.open}
                              onChange={(e) => updateDayHours(key, "open", e.target.value)}
                              disabled={readOnly}
                              className="w-32 rounded-lg border border-tribal-border bg-black/30 px-2 py-1.5 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                            />
                            <span className="text-xs text-tribal-muted">to</span>
                            <input
                              type="time"
                              value={day.close}
                              onChange={(e) => updateDayHours(key, "close", e.target.value)}
                              disabled={readOnly}
                              className="w-32 rounded-lg border border-tribal-border bg-black/30 px-2 py-1.5 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                            />
                          </div>
                        ) : (
                          <span className="text-xs italic text-tribal-muted/60">Closed</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <Field label="Timezone">
                <select
                  value={draft.timezone ?? "America/New_York"}
                  onChange={(e) => setField("timezone", e.target.value)}
                  disabled={readOnly}
                  className="w-full rounded-lg border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Forward To Number" hint="Staff answer line">
                <input
                  type="tel"
                  value={draft.forward_to_number ?? ""}
                  onChange={(e) => setField("forward_to_number", e.target.value)}
                  disabled={readOnly}
                  placeholder="+15551234567"
                  className="w-full rounded-lg border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                />
              </Field>

              <Field label="Ring Timeout">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={5}
                    max={120}
                    value={draft.ring_timeout_seconds ?? 20}
                    onChange={(e) =>
                      setField("ring_timeout_seconds", parseInt(e.target.value, 10) || 20)
                    }
                    disabled={readOnly}
                    className="w-24 rounded-lg border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                  />
                  <span className="text-xs text-tribal-muted">seconds</span>
                </div>
              </Field>

              <Field label="Greeting Message" hint="Plays when call connects before dialing staff">
                <textarea
                  rows={2}
                  value={draft.greeting_message ?? ""}
                  onChange={(e) => setField("greeting_message", e.target.value)}
                  disabled={readOnly}
                  className="w-full resize-none rounded-lg border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                />
              </Field>

              <Field
                label="Voicemail Prompt"
                hint="Played when staff doesn't answer during hours"
              >
                <textarea
                  rows={2}
                  value={draft.voicemail_prompt ?? ""}
                  onChange={(e) => setField("voicemail_prompt", e.target.value)}
                  disabled={readOnly}
                  className="w-full resize-none rounded-lg border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                />
              </Field>

              <Field
                label="After Hours Message"
                hint="Played when call arrives outside business hours"
              >
                <textarea
                  rows={2}
                  value={draft.after_hours_message ?? ""}
                  onChange={(e) => setField("after_hours_message", e.target.value)}
                  disabled={readOnly}
                  className="w-full resize-none rounded-lg border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                />
              </Field>

              <Field label="Voicemail Greeting" hint="Plays immediately before the recording beep">
                <textarea
                  rows={2}
                  value={draft.voicemail_greeting ?? ""}
                  onChange={(e) => setField("voicemail_greeting", e.target.value)}
                  disabled={readOnly}
                  className="w-full resize-none rounded-lg border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                />
              </Field>
            </div>
          </section>
        )}

        {!readOnly && draft && (
          <div className="flex items-center justify-end gap-3 pb-8">
            {saved && <span className="text-sm text-green-400">Voice settings saved.</span>}
            {saveError && <span className="text-sm text-red-400">{saveError}</span>}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !isDirty}
              className="rounded-lg bg-tribal-red px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Voice Settings"}
            </button>
          </div>
        )}
        {readOnly && draft && (
          <p className="pb-8 text-xs text-tribal-muted">
            Read-only — ask an admin to change voice settings.
          </p>
        )}
      </div>

      {newLineOpen && !readOnly && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-line-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          onClick={(e) => {
            // Click-outside to close, but only if we're not mid-submit.
            if (e.target === e.currentTarget && !newLineSubmitting) setNewLineOpen(false);
          }}
        >
          <div className="w-full max-w-md rounded-xl border border-tribal-border bg-tribal-surface shadow-2xl">
            <div className="border-b border-tribal-border px-5 py-4">
              <h3 id="new-line-title" className="text-base font-semibold">
                Provision new phone line
              </h3>
              <p className="mt-0.5 text-xs text-tribal-muted">
                Creates a new voice settings row. After saving, set business hours and
                greetings on the line's settings page.
              </p>
            </div>
            <div className="space-y-4 px-5 py-5">
              <Field label="Twilio phone line" hint="E.164 format, e.g. +17575550100">
                <input
                  type="tel"
                  autoFocus
                  value={newLinePhone}
                  onChange={(e) => setNewLinePhone(e.target.value)}
                  disabled={newLineSubmitting}
                  placeholder="+17575550100"
                  className="w-full rounded-lg border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                />
              </Field>
              <Field label="Forward to number" hint="Staff answer line during business hours">
                <input
                  type="tel"
                  value={newLineForward}
                  onChange={(e) => setNewLineForward(e.target.value)}
                  disabled={newLineSubmitting}
                  placeholder="+17575550100"
                  className="w-full rounded-lg border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none disabled:cursor-not-allowed"
                />
              </Field>
              {newLineError && (
                <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  {newLineError}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-tribal-border px-5 py-4">
              <button
                type="button"
                onClick={() => setNewLineOpen(false)}
                disabled={newLineSubmitting}
                className="rounded-lg px-3 py-2 text-sm text-tribal-muted transition-colors hover:text-white disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateLine}
                disabled={newLineSubmitting}
                className="rounded-lg bg-tribal-red px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:opacity-50"
              >
                {newLineSubmitting ? "Creating…" : "Create line"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-tribal-muted">{label}</label>
      {hint && <p className="mb-1 text-[11px] text-tribal-muted/70">{hint}</p>}
      {children}
    </div>
  );
}

// Make sure every VOICE_DAYS key has an entry so rendering doesn't jump when
// the first toggle flips a missing key into existence.
function normalizeHours(input: VoiceBusinessHours | null): VoiceBusinessHours {
  const hours: VoiceBusinessHours = { ...(input ?? {}) };
  for (const { key } of VOICE_DAYS) {
    if (!hours[key]) {
      hours[key] = { open: "09:00", close: "21:00", enabled: false };
    }
  }
  return hours;
}
